import React, { useState, useEffect, useMemo, useRef } from 'react';
import useSimulationStore from '../../store/simulationStore';
import { shallow } from 'zustand/shallow'; // Import shallow
// Removed Particle import, not directly needed

// Import the type definition used in App.tsx
type WorkerParticleState = {
  id: string;
  speciesName: string;
  isActive: boolean;
};

interface LogEntry {
  timestamp: string;
  counts: Record<string, number>; // { 'SpeciesName': count }
  activeTotal: number;
}

const MAX_LOG_ENTRIES = 50; // Keep the log from growing indefinitely

// Helper function for string padding
const pad = (str: string | number, width: number, alignRight = false): string => {
  const s = String(str);
  if (alignRight) {
    return s.padStart(width, ' ');
  } else {
    return s.padEnd(width, ' ');
  }
};

// Type for header widths map
interface HeaderWidths {
  Time: number;
  Total: number; // Add Total column
  [speciesName: string]: number; // Use speciesName
}

// Define Props for the component
interface StatisticsDisplayProps {
    particleStates: WorkerParticleState[];
}

const StatisticsDisplay: React.FC<StatisticsDisplayProps> = ({ particleStates }) => {
  const [logEntries, setLogEntries] = useState<LogEntry[]>([]);
  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<'counts' | 'energy' | 'energyGraph'>('counts');
  const [energyHistory, setEnergyHistory] = useState<{
    total: number[];
    translational: number[];
    rotational: number[];
    timestamps: string[];
  }>({ total: [], translational: [], rotational: [], timestamps: [] });
  const lastLoggedCountsRef = useRef<string | null>(null); // Store stringified counts to detect change
  const lastEnergyUpdateRef = useRef<number>(0); // Throttle energy history updates
  
  // Still need chemicalSpecies for headers and resetCount to clear log
  const { chemicalSpeciesForHeaders, resetCount, totalEnergy, translationalEnergy, rotationalEnergy, initialTotalEnergy, energyRatio, thermostatEnabled } = useSimulationStore(
    state => ({ 
        chemicalSpeciesForHeaders: state.controls.chemicalSpecies, 
        resetCount: state.resetCount,
        totalEnergy: state.totalEnergy,
        translationalEnergy: state.translationalEnergy,
        rotationalEnergy: state.rotationalEnergy,
        initialTotalEnergy: state.initialTotalEnergy,
        energyRatio: state.energyRatio,
        thermostatEnabled: state.thermostatEnabled
    }), 
    shallow 
  );

  // --- Calculate Counts from Props --- 
  const currentCounts = useMemo(() => {
      let activeTotal = 0;
      const speciesCounts: { [speciesName: string]: number } = {};
      
      // Initialize counts for all defined species to ensure they appear in the log
      chemicalSpeciesForHeaders.forEach(species => {
          speciesCounts[species.name] = 0;
      });
      
      // Calculate counts from the received states
      for (const p of particleStates) {
          if (p.isActive) {
              activeTotal++;
              // Ensure we don't count 'inactive' placeholder if it exists
              if (p.speciesName !== 'inactive' && speciesCounts.hasOwnProperty(p.speciesName)) {
                  speciesCounts[p.speciesName]++;
              }
          }
      }
      return { activeTotal, speciesCounts };
  }, [particleStates, chemicalSpeciesForHeaders]); // Recalculate when props or definitions change

  // --- Effect for Logging based on changes in calculated counts --- 
  useEffect(() => {
    const countsString = JSON.stringify(currentCounts.speciesCounts);
    // Only add log entry if counts have actually changed since last log
    if (countsString !== lastLoggedCountsRef.current) {
        const now = new Date();
        const timestamp = now.toLocaleTimeString();
        setLogEntries(prevEntries => {
            const newEntry: LogEntry = { 
                timestamp, 
                counts: currentCounts.speciesCounts, 
                activeTotal: currentCounts.activeTotal 
            }; 
            const updatedEntries = [newEntry, ...prevEntries]; 
            // Limit log size
            return updatedEntries.slice(0, MAX_LOG_ENTRIES); 
        });
        lastLoggedCountsRef.current = countsString; // Update last logged counts
    }
    // Depend on the calculated counts object (its reference changes if values change)
  }, [currentCounts]); 

  // --- Effect for Clearing Log on Reset --- 
  useEffect(() => {
    // Skip initial mount
    if (lastLoggedCountsRef.current === null) {
        lastLoggedCountsRef.current = JSON.stringify(currentCounts.speciesCounts); // Initialize ref
        return;
    }
    console.log("[Stats] Simulation reset detected, clearing statistics log.");
    setLogEntries([]); // Clear the log entries
    setEnergyHistory({ total: [], translational: [], rotational: [], timestamps: [] }); // Clear energy history
    lastLoggedCountsRef.current = null; // Reset last logged counts

  }, [resetCount]); // Depend only on resetCount

  // --- Effect for Energy History Tracking (Throttled to 1/sec) ---
  useEffect(() => {
    if (totalEnergy > 0 || translationalEnergy > 0 || rotationalEnergy > 0) {
      const now = Date.now();
      
      // Only update once per second to avoid memory leak
      if (now - lastEnergyUpdateRef.current < 1000) return;
      
      lastEnergyUpdateRef.current = now;
      const timestamp = new Date().toLocaleTimeString();
      
      setEnergyHistory(prev => {
        const newHistory = {
          total: [...prev.total.slice(-299), totalEnergy], // Keep 5 minutes of data (300 points at 1/sec)
          translational: [...prev.translational.slice(-299), translationalEnergy],
          rotational: [...prev.rotational.slice(-299), rotationalEnergy],
          timestamps: [...prev.timestamps.slice(-299), timestamp]
        };
        return newHistory;
      });
    }
  }, [totalEnergy, translationalEnergy, rotationalEnergy]);

  const toggleExpansion = () => {
    setIsExpanded(!isExpanded);
  };

  // --- Column Width Calculation and Formatting --- 
  const timeWidth = 12;
  const totalWidth = 8;
  const countWidth = 8; 
  const columnSeparator = '  '; 

  const allSpeciesHeaders = chemicalSpeciesForHeaders.map(s => s.name);

  const headerWidths: HeaderWidths = {
    Time: timeWidth,
    Total: totalWidth,
    ...Object.fromEntries(allSpeciesHeaders.map(name => [
      name, 
      Math.max(name.length, countWidth)
    ]))
  };

  // Add 'Total' to the ordered headers
  const orderedHeaders = ['Time', 'Total', ...allSpeciesHeaders];

  const formattedHeader = orderedHeaders.map(header => 
      pad(header, headerWidths[header])
  ).join(columnSeparator);

  const formattedLogEntries = logEntries.map((entry, index) => {
    const rowData = [
      pad(entry.timestamp, headerWidths.Time), 
      pad(entry.activeTotal, headerWidths.Total, true), // Add Total count
      ...allSpeciesHeaders.map(headerName => 
        pad(entry.counts[headerName] ?? 0, headerWidths[headerName], true)
      )
    ];
    return (
      <React.Fragment key={`${entry.timestamp}-${index}`}>
        {rowData.join(columnSeparator) + '\n'}
      </React.Fragment>
    );
  });
  // --- End Column Formatting ---

  return (
    <div className="stats-display-container" style={containerStyle}>
      {/* Header bar for expand/collapse and tabs */} 
      <div style={headerStyle}> 
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span>Statistics</span>
          <div style={{ display: 'flex', gap: '5px' }}>
            <button 
              style={{...tabButtonStyle, ...(activeTab === 'counts' ? activeTabStyle : {})}}
              onClick={() => setActiveTab('counts')}
            >
              Counts
            </button>
            <button 
              style={{...tabButtonStyle, ...(activeTab === 'energy' ? activeTabStyle : {})}}
              onClick={() => setActiveTab('energy')}
            >
              Energy
            </button>
            <button 
              style={{...tabButtonStyle, ...(activeTab === 'energyGraph' ? activeTabStyle : {})}}
              onClick={() => setActiveTab('energyGraph')}
            >
              Energy Graph
            </button>
          </div>
        </div>
        <button style={buttonStyle} onClick={toggleExpansion}>{isExpanded ? '-' : '+'}</button>
      </div>
      {/* Conditionally render the content area */} 
      {isExpanded && (
        <div style={logAreaStyle}>
          {activeTab === 'counts' ? (
            <>
              {/* Static Formatted Header Row - Prevent shrinking */}
              <pre style={{ ...preStyle, ...headerRowStyle }}>
                {formattedHeader + '\n'} 
              </pre>
              {/* Scrolling Data Rows */}
              <div style={contentStyle}>
                <pre style={preStyle}>
                  {formattedLogEntries} {/* Render the pre-formatted rows */}
                </pre>
              </div>
            </>
          ) : activeTab === 'energy' ? (
            /* Energy Tab Content */
            <div style={energyContentStyle}>
              <div style={energyInfoStyle}>
                <div>Total Energy: {totalEnergy.toFixed(2)}</div>
                <div>Translational: {translationalEnergy.toFixed(2)}</div>
                <div>Rotational: {rotationalEnergy.toFixed(2)}</div>
                <div>Initial: {initialTotalEnergy.toFixed(2)}</div>
                <div>Ratio: {energyRatio.toFixed(3)}</div>
                <div>Thermostat: {thermostatEnabled ? 'ON' : 'OFF'}</div>
              </div>
              <div style={energyChartStyle}>
                <div style={{ fontSize: '12px', color: '#ccc', marginBottom: '5px' }}>
                  Energy Over Time (Last {energyHistory.total.length} points)
                </div>
                <div style={{ fontSize: '10px', color: '#888' }}>
                  Total: {energyHistory.total.length > 0 ? energyHistory.total[energyHistory.total.length - 1].toFixed(2) : '0.00'}
                  {' | '}
                  Trans: {energyHistory.translational.length > 0 ? energyHistory.translational[energyHistory.translational.length - 1].toFixed(2) : '0.00'}
                  {' | '}
                  Rot: {energyHistory.rotational.length > 0 ? energyHistory.rotational[energyHistory.rotational.length - 1].toFixed(2) : '0.00'}
                </div>
              </div>
            </div>
          ) : (
            // Energy Graph Tab
            <div style={{ padding: '8px', height: '100%' }}>
              <div style={{ fontSize: '12px', color: '#ccc', marginBottom: '6px' }}>Energy Graph (Total, Translational, Rotational)</div>
              <div style={{ background: 'rgba(30,30,30,0.8)', border: '1px solid #444', borderRadius: 4, padding: 6, height: '100%' }}>
                {(() => {
                  const width = 520; // fixed chart width
                  const height = 120; // fixed chart height to fit panel
                  const paddingLeft = 30, paddingRight = 10, paddingTop = 10, paddingBottom = 18;
                  const n = energyHistory.total.length;
                  const slice = n; // plot all collected points
                  const total = energyHistory.total.slice(-slice);
                  const trans = energyHistory.translational.slice(-slice);
                  const rot = energyHistory.rotational.slice(-slice);
                  const allVals = [...total, ...trans, ...rot];
                  const minY = allVals.length ? Math.min(...allVals) : 0;
                  const maxY = allVals.length ? Math.max(...allVals) : 1;
                  const y0 = minY === maxY ? minY - 0.5 : minY;
                  const y1 = minY === maxY ? maxY + 0.5 : maxY;
                  const innerW = width - paddingLeft - paddingRight;
                  const innerH = height - paddingTop - paddingBottom;
                  const xScale = (i: number) => paddingLeft + (innerW * (slice <= 1 ? 0 : i / (slice - 1)));
                  const yScale = (v: number) => paddingTop + innerH * (1 - (v - y0) / (y1 - y0));
                  const toPath = (arr: number[]) => arr.map((v, i) => `${i === 0 ? 'M' : 'L'} ${xScale(i)} ${yScale(v)}`).join(' ');
                  return (
                    <svg width={width} height={height} style={{ display: 'block', width: '100%' }}>
                      {/* Axes */}
                      <line x1={paddingLeft} y1={paddingTop} x2={paddingLeft} y2={paddingTop + innerH} stroke="#555" strokeWidth={1} />
                      <line x1={paddingLeft} y1={paddingTop + innerH} x2={paddingLeft + innerW} y2={paddingTop + innerH} stroke="#555" strokeWidth={1} />
                      {/* Series */}
                      <path d={toPath(total)} fill="none" stroke="#4fc3f7" strokeWidth={1.5} />
                      <path d={toPath(trans)} fill="none" stroke="#81c784" strokeWidth={1.5} />
                      <path d={toPath(rot)} fill="none" stroke="#ffb74d" strokeWidth={1.5} />
                      {/* Legend */}
                      <g>
                        <rect x={paddingLeft + 6} y={paddingTop + 6} width={10} height={2} fill="#4fc3f7" />
                        <text x={paddingLeft + 20} y={paddingTop + 9} fill="#bbb" fontSize={10}>Total</text>
                        <rect x={paddingLeft + 70} y={paddingTop + 6} width={10} height={2} fill="#81c784" />
                        <text x={paddingLeft + 84} y={paddingTop + 9} fill="#bbb" fontSize={10}>Trans</text>
                        <rect x={paddingLeft + 130} y={paddingTop + 6} width={10} height={2} fill="#ffb74d" />
                        <text x={paddingLeft + 144} y={paddingTop + 9} fill="#bbb" fontSize={10}>Rot</text>
                      </g>
                      {/* Y min/max labels */}
                      <text x={2} y={paddingTop + innerH} fill="#777" fontSize={9}>{y0.toFixed(1)}</text>
                      <text x={2} y={paddingTop + 10} fill="#777" fontSize={9}>{y1.toFixed(1)}</text>
                    </svg>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Basic Inline Styles
const containerStyle: React.CSSProperties = {
  position: 'absolute',
  bottom: '10px',
  right: '10px',
  width: 'auto', // Allow width to expand based on content
  minWidth: '300px', // Prevent it from becoming too narrow
  maxWidth: '80vw', // Allow expansion up to 80% of viewport width
  maxHeight: '200px',
  backgroundColor: 'rgba(40, 40, 40, 0.8)',
  color: '#eee',
  border: '1px solid #555',
  borderRadius: '4px',
  fontSize: '12px',
  fontFamily: 'monospace',
  zIndex: 100, 
  display: 'flex', // Keep this flex for vertical layout of header bar + log area
  flexDirection: 'column',
};

const headerStyle: React.CSSProperties = {
  padding: '4px 8px',
  backgroundColor: 'rgba(60, 60, 60, 0.9)',
  cursor: 'pointer',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  fontWeight: 'bold',
  flexShrink: 0, // Prevent header bar from shrinking
};

// New style for the area containing the header row and scrolling content
const logAreaStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1, // Allow this area to take remaining vertical space
    minHeight: 0, // Crucial for allowing contentStyle to shrink properly
};

const headerRowStyle: React.CSSProperties = {
    padding: '8px 8px',
    fontWeight: 'bold',
    borderBottom: '1px solid #666',
    backgroundColor: 'rgba(50, 50, 50, 0.8)',
    whiteSpace: 'pre',
    overflowX: 'hidden',
    flexShrink: 0, // Prevent this header row from shrinking vertically
};

const contentStyle: React.CSSProperties = {
  overflowY: 'scroll',
  overflowX: 'auto',   
  padding: '0 8px 4px 8px',
  flexGrow: 1, // Allow scrolling area to take remaining space within logArea
  minHeight: 0, 
};

const buttonStyle: React.CSSProperties = {
  background: 'none',
  border: '1px solid #888',
  color: '#eee',
  cursor: 'pointer',
  padding: '0px 4px',
  marginLeft: '10px',
  lineHeight: '1',
};

const preStyle: React.CSSProperties = {
    margin: 0, // Remove default pre margin
    whiteSpace: 'pre' // Prevent wrapping to enable horizontal scroll
};

// New styles for tabs and energy content
const tabButtonStyle: React.CSSProperties = {
  background: 'rgba(60, 60, 60, 0.8)',
  border: '1px solid #666',
  color: '#ccc',
  cursor: 'pointer',
  padding: '2px 8px',
  fontSize: '11px',
  borderRadius: '2px'
};

const activeTabStyle: React.CSSProperties = {
  background: 'rgba(80, 80, 80, 0.9)',
  border: '1px solid #888',
  color: '#fff'
};

const energyContentStyle: React.CSSProperties = {
  padding: '8px',
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  height: '100%'
};

const energyInfoStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '5px',
  fontSize: '11px',
  color: '#ccc'
};

const energyChartStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: 'rgba(30, 30, 30, 0.8)',
  borderRadius: '4px',
  padding: '10px',
  border: '1px solid #444'
};

export default StatisticsDisplay; 